/**
 * talentMobilityEmployees.utils.js
 *
 * Maps GET /api/talent-mobility/employees responses (see
 * getTalentMobilityEmployees in Api/apiService.js) into the row shape
 * TalentMobilityManagerV2.jsx's table columns expect. Kept as pure
 * functions, separate from useTalentMobilityData.js's fetch/state logic, so
 * the field mapping — the part most likely to need a tweak whenever the API
 * response shape changes — can be read and unit-tested on its own.
 */

import { JOB_OPENING_STATUS, ROLE_STATUS_CLOSED, ROLE_STATUS_OPEN } from "./talentMobility.constants";

// The API's `priority` is the JD's critical_role flag — "true"/"false"
// strings (or booleans). Translate to the High/Normal labels the Priority
// column renders (PRIORITY_TAG_COLOR keys); an already-labeled value passes
// through untouched.
const toPriorityLabel = (priority) => {
  if (priority === true || String(priority).toLowerCase() === "true") return "High";
  if (!priority || String(priority).toLowerCase() === "false") return "Normal";
  return priority;
};

/**
 * Maps one API employee record to a table row.
 *
 * `roleId` is the JD's own id (jd_sb_unique_id) doing double duty as this
 * row's "role" identifier — the API has no separate "role" concept, a JD
 * *is* the role here. It drives the row-click navigation to
 * /talent-mobility/role/:roleId/:userId (userId = this row's `id`, so
 * useRoleSuccessors can re-find the exact employee row). `id` is
 * updateRoleField's lookup key — the Open/Discoverable toggles are saved per
 * employee (sb_unique_id + user_id), not per JD.
 *
 * `requisitionNumber` is the API's `sb_requisition_number` — the human-facing
 * "Requisition Number" column. `roleId` stays the opaque navigation key above;
 * it's never displayed.
 *
 * `employeeId` is the human-readable HRIS code (`emp_id`, e.g.
 * "SB-ORG-DEMO-012") — the display id. It can be null; columns render "—"
 * then. The opaque `userId` is never displayed, it only drives `id`.
 *
 * `matchCount` is the API's `matches_count` — how many candidates clear the
 * match threshold for this row's role (drives the "Matches (>N%)" column).
 *
 * `isVacant` is true for the API's synthetic "no occupant" row (a published
 * JD with nobody ACTIVE on it — see talent_mobility_employees_service.py's
 * _serialize_vacant_role) — `userId` is the only field the API guarantees
 * null exclusively for that row, never for a real employee.
 */
export const mapEmployeeToTableRow = (employee) => ({
  id: employee.userId,
  roleId: employee.jd_sb_unique_id,
  requisitionNumber: employee.sb_requisition_number,
  employeeId: employee.emp_id,
  name: employee.name,
  title: employee.title,
  businessUnit: employee.businessUnit,
  function: employee.function,
  subFunction: employee.subFunction,
  location: employee.location,
  level: employee.level,
  priority: toPriorityLabel(employee.priority),
  status: employee.jobOpeningStatus === JOB_OPENING_STATUS.OPEN ? ROLE_STATUS_OPEN : ROLE_STATUS_CLOSED,
  discoverable: Boolean(employee.discoverable),
  matchCount: employee.matches_count ?? 0,
  isVacant: employee.userId == null,
});

/** Maps the full GET /talent-mobility/employees response body to table rows. */
export const mapEmployeesResponseToTableRows = (response) => {
  const employees = response?.employees;
  return Array.isArray(employees) ? employees.map(mapEmployeeToTableRow) : [];
};

/**
 * Groups mapEmployeeToTableRow's rows by JD into TalentMobilityManagerV2's
 * role rows — one row per job (jd_sb_unique_id), expandable into a sub-table
 * of that role's employees.
 *
 * `jobId` is the JD's own id, used for row-click navigation only.
 * `requisitionNumber` (read off the first record of the group, same as
 * `title`/`priority`/etc. below) is what the "Requisition Number" column
 * actually renders, shown instead of any one employee's Employee ID/Name (a
 * role has many). `openCount`/`discoverableCount` roll up how many
 * of the role's employees are individually Open/Discoverable — those toggles
 * stay per-employee (updateRoleField), only their count shows at the role
 * level. `employees` carries the group's own rows verbatim, rendered by the
 * accordion's sub-table with their own editable Open/Discoverable checkboxes.
 * `title`/`priority`/etc. are read off the first record of the group — every
 * assignment against the same JD shares them (same as
 * mapEmployeesResponseToRedeploymentRoles below).
 *
 * A vacant role's group holds exactly one row — the API's isVacant
 * placeholder (see mapEmployeeToTableRow) — never mixed with real
 * employees, since the API only emits that placeholder for a JD with zero
 * ACTIVE occupants. The group's own `isVacant` flag is what
 * TalentMobilityRoleEmployeesTable checks to show its empty-state message
 * instead of a one-row table with blank Employee ID/Name; employeeCount/
 * openCount/discoverableCount are all forced to 0 for it too — the
 * placeholder isn't a real employee, so it shouldn't count as one "1/1"
 * open/discoverable person the outer table's columns would otherwise show.
 */
export const groupTableRowsByRole = (employees) => {
  const rolesByJdId = new Map();

  employees.forEach((employee) => {
    const jdId = employee.roleId;
    if (!jdId) return;

    const existingRole = rolesByJdId.get(jdId);
    if (existingRole) {
      existingRole.employees.push(employee);
      existingRole.matchCount = Math.max(existingRole.matchCount, employee.matchCount);
      return;
    }

    rolesByJdId.set(jdId, {
      id: jdId,
      jobId: jdId,
      requisitionNumber: employee.requisitionNumber,
      title: employee.title,
      businessUnit: employee.businessUnit,
      function: employee.function,
      subFunction: employee.subFunction,
      location: employee.location,
      level: employee.level,
      priority: employee.priority,
      matchCount: employee.matchCount,
      employees: [employee],
    });
  });

  return [...rolesByJdId.values()].map((role) => {
    const isVacant = role.employees.length === 1 && role.employees[0].isVacant;
    return {
      ...role,
      isVacant,
      employeeCount: isVacant ? 0 : role.employees.length,
      openCount: isVacant ? 0 : role.employees.filter((employee) => employee.status === ROLE_STATUS_OPEN).length,
      discoverableCount: isVacant ? 0 : role.employees.filter((employee) => employee.discoverable).length,
    };
  });
};

// ─── Redeployment Center row shapes ──────────────────────────────────────────
// The same GET /talent-mobility/employees response feeds both Redeployment
// Center views: Employee View renders it one row per person, Role View
// groups it by JD (one row per role, with a live occupant count).

/**
 * Maps one API employee record to a Redeployment Center "Employee View" row.
 *
 * `nextRoles: null` means "not loaded yet" — API rows fetch their candidate
 * roles lazily from POST /talent-mobility/next-roles when the row is
 * expanded (see useNextRolesForPerson), unlike mock rows which carry a
 * precomputed array. `nextRolesCount` is the API's next_roles_count — the
 * same number of rows that lazy fetch will return — so the "Potential Next
 * Roles" column can show a real count without N upfront requests.
 *
 * `currentRoleId` (the person's own JD's jd_sb_unique_id) keys the
 * redeployment plan the row's actions write to (useRedeploymentPlanStore) —
 * same field name mock PEOPLE rows already carry.
 */
export const mapEmployeeToRedeploymentPersonRow = (employee) => ({
  id: employee.userId,
  // Display id is the HRIS emp_id (nullable), never the opaque userId.
  employeeId: employee.emp_id,
  name: employee.name,
  currentRoleId: employee.jd_sb_unique_id,
  currentRoleTitle: employee.title || "—",
  legalEntity: employee.legalEntity,
  businessUnit: employee.businessUnit,
  function: employee.function,
  subFunction: employee.subFunction,
  level: employee.level,
  location: employee.location,
  nextRoles: null,
  nextRolesCount: employee.next_roles_count ?? null,
});

/** Maps the GET /talent-mobility/employees response to Employee View rows. */
export const mapEmployeesResponseToRedeploymentPeople = (response) => {
  const employees = response?.employees;
  return Array.isArray(employees) ? employees.map(mapEmployeeToRedeploymentPersonRow) : [];
};

/**
 * Groups the GET /talent-mobility/employees response by JD into Redeployment
 * Center "Role View" rows. The endpoint returns one record per (employee, JD)
 * assignment, so:
 *
 *   - `currentPositionCount` is the number of employees holding the JD today
 *     (real headcount, counted from the assignment records);
 *   - `targetPositionCount` starts equal to it — "no reduction planned" —
 *     and only changes through the on-screen Target Positions editor;
 *   - `matchCount` is the JD's matches_count (identical on every record of
 *     the group — max() just guards against inconsistent rows);
 *   - `candidates` is empty — per-role successor lists come from
 *     /role-successor-analytics, not this endpoint, so the expanded panel
 *     shows its empty message rather than invented candidates.
 */
export const mapEmployeesResponseToRedeploymentRoles = (response) => {
  const employees = Array.isArray(response?.employees) ? response.employees : [];
  const rolesByJdId = new Map();

  employees.forEach((employee) => {
    const jdId = employee.jd_sb_unique_id;
    if (!jdId) return;

    const existingRole = rolesByJdId.get(jdId);
    if (existingRole) {
      existingRole.currentPositionCount += 1;
      existingRole.targetPositionCount += 1;
      existingRole.matchCount = Math.max(existingRole.matchCount, employee.matches_count ?? 0);
      return;
    }

    rolesByJdId.set(jdId, {
      id: jdId,
      title: employee.title || "—",
      legalEntity: employee.legalEntity,
      businessUnit: employee.businessUnit,
      function: employee.function,
      subFunction: employee.subFunction,
      level: employee.level,
      location: employee.location,
      // The JD's critical_role flag as the High/Normal label Workforce
      // Planning's Priority column renders — identical on every record of
      // the group, so the first one wins.
      priority: toPriorityLabel(employee.priority),
      currentPositionCount: 1,
      targetPositionCount: 1,
      matchCount: employee.matches_count ?? 0,
      candidates: [],
    });
  });

  return [...rolesByJdId.values()];
};
